import React, { useState, useEffect } from "react";
import { Joyride } from 'react-joyride';
import usePageTitle from 'hooks/usePageTitle';
import { getJoyrideScrollOffset } from 'utils/joyrideConfig';
import PageMenu from "../../blocks/PageMenu/PageMenu";
import Icon from "../../blocks/Icon/Icon";
import CreateCommunicationModal from "../../blocks/CreateCommunicationModal/CreateCommunicationModal";
import CommsList from "../../blocks/CommsList/CommsList";
import Loader from "components/blocks/Loader/Loader";
import useDecode from "hooks/useDecode";
import useGetAllUserCommunicationByUserId from "../../../services/communications/useGetAllUserCommunicationByUserId";
import useModal from "../../../hooks/useModal";
import useCreateGeneralCommunication from "services/communications/useCreateGeneralCommunication";
import useGetRole from "hooks/useGetRole";
import { useCommunicationsTour } from "hooks/useCommunicationsTour";
import "./Communications.scss";

const JOYRIDE_PROPS = {
  continuous: true,
  scrollToFirstStep: true,
  locale: { back: 'Anterior', close: 'Cerrar', last: 'Finalizar', next: 'Siguiente', nextWithProgress: 'Siguiente ({current} de {total})', open: 'Iniciar tour de ayuda', skip: 'Omitir tour' },
  options: { primaryColor: '#0F1F3D', zIndex: 10000, overlayColor: 'rgba(15,47,73,0.45)', showProgress: true, buttons: ['back', 'skip', 'close', 'primary'] } as any,
  styles: { beaconInner: { backgroundColor: '#F97316' }, beaconOuter: { borderColor: '#F97316', backgroundColor: 'rgba(249,115,22,0.2)' }, tooltip: { borderRadius: 8, fontSize: 14 }, tooltipTitle: { fontSize: 15, fontWeight: 700, textAlign: 'left' as const }, tooltipContent: { textAlign: 'left' as const } },
};

interface IdataComunication {
  asunto: string;
  mensaje: string;
  userId: string;
  externalUserId: string | null;
  prestacionId: string;
  files: any[];
  comunicacionReferenciaId: string | null;
}

const Communications: React.FC = () => {
  usePageTitle('Comunicaciones');
  const {
    getAllUserCommunication,
    allUserCommunication,
    loadingGetAllUserCommunication,
    errorGetAllUserCommunication,
    isComunicacionHabilitada,
  } = useGetAllUserCommunicationByUserId();
  const { createGeneralCommunication, successCreateGeneralCommunication } = useCreateGeneralCommunication();
  const { collapsed, handleCloseModal, handleOpenModal } = useModal();
  const [isLoading, setIsLoading] = useState(true);
  const [toast, setToast] = useState<string | null>(null);
  const tokenResponse = useDecode("token");
  const userId = tokenResponse && tokenResponse.extension_id;
  const externalUserId = tokenResponse?.extension_external_id;
  const { isImpersonation } = useGetRole;

  const { run, tourKey, steps, handleCallback, startTour } = useCommunicationsTour(!!isComunicacionHabilitada && !isImpersonation());

  useEffect(() => {
    const handler = () => startTour();
    window.addEventListener('solimat:startCommunicationsTour', handler);
    return () => window.removeEventListener('solimat:startCommunicationsTour', handler);
  }, [startTour]);

  useEffect(() => {
    getAllUserCommunication(userId);
  }, [successCreateGeneralCommunication]);

  useEffect(() => {
    if (allUserCommunication.length === 0 || loadingGetAllUserCommunication) {
      setIsLoading(false);
    }
  }, [allUserCommunication]);

  const convertBase64 = async (files: File[]) => {
    if (!files || files.length === 0) return [];
    return Promise.all(
      files.map(
        (file) =>
          new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => {
              const parts = file.name.split(".");
              resolve({
                name: file.name,
                extension: parts.pop(),
                externalUri: "",
                base64: (reader.result as string).split(",")[1],
                externalId: "",
              });
            };
            reader.onerror = reject;
          })
      )
    );
  };

  const handleModalSubmit = async (data: { asunto: string; mensaje: string; files: File[] }) => {
    const dataFiles = await convertBase64(data.files);
    const payload: IdataComunication = {
      asunto: data.asunto,
      mensaje: data.mensaje,
      userId,
      externalUserId,
      prestacionId: null,
      files: dataFiles,
      comunicacionReferenciaId: null,
    };
    createGeneralCommunication(userId, payload, () => {
      getAllUserCommunication(userId);
      handleCloseModal();
      setToast("Comunicación enviada correctamente");
      setTimeout(() => setToast(null), 3000);
    });
  };

  return (
    <>
      <Joyride
        key={tourKey}
        steps={steps}
        run={run}
        onEvent={handleCallback}
        {...JOYRIDE_PROPS}
        scrollOffset={getJoyrideScrollOffset()}
      />

      {!isImpersonation() && (
        <PageMenu title="Comunicaciones" subtitle="Comunicaciones, confirmaciones y trámites" breadcrumbs={["Comunicaciones"]}>
          <div className="btns-header-container" data-tour="comms-create-btn">
            {isComunicacionHabilitada && (
              <button className="btn-box btn-outline-accent" onClick={handleOpenModal}>
                <span className="e-icons e-add-new"></span>
                <span>Crear comunicación</span>
              </button>
            )}
          </div>
        </PageMenu>
      )}
      <div className="container">
        {errorGetAllUserCommunication ? (
          <p className="comms-error">Lo sentimos, se ha producido un error.</p>
        ) : (isLoading || loadingGetAllUserCommunication) ? (
          <Loader activeLoader label="Descargando comunicaciones" />
        ) : (
          <CommsList arrData={allUserCommunication} />
        )}
      </div>

      {!collapsed && !isImpersonation() && (
        <CreateCommunicationModal
          onClose={handleCloseModal}
          onSubmit={handleModalSubmit}
        />
      )}

      {toast && (
        <div className="notif-toast notif-toast--success" role="status">
          <Icon name="check" size={18} /><span>{toast}</span>
        </div>
      )}
    </>
  );
};

export default Communications;
